import { useMemo } from "react";
import type { IOperationResponse, IPreQuoteRequest } from "../services/api";
import type { IWidgetSession } from "../shared/session/interfaces/session";
import { usePreQuote } from "./usePreQuote";
import { resolveDestinationCountry } from "../utils/destinationCountry";

const SENTINEL_AMOUNT = 100;

const formatRate = (rate: number): string => rate.toFixed(4);

export interface HomeExchangeRate {
  countryIso2: string;
  rate: string;
  currency: string;
}

/**
 * One-shot indicative FX for the transactions home. Last operation selects
 * the destination country; pre-quote supplies a current (non-binding) rate.
 * Does not poll.
 */
export const useHomeExchangeRate = (
  operations: IOperationResponse[],
  session: IWidgetSession | null
): { card: HomeExchangeRate | null; loading: boolean } => {
  const latest = operations[0];

  const destinationCountry = useMemo(
    () => resolveDestinationCountry(latest, session),
    [latest, session]
  );

  const originCountry = useMemo(() => {
    const corridor =
      session?.config?.defaultCorridor || session?.config?.corridors?.[0];
    return corridor?.originCountryCode?.trim().toUpperCase() || "US";
  }, [session]);

  const input = useMemo<IPreQuoteRequest | null>(() => {
    if (!latest || !destinationCountry) return null;
    const amount =
      typeof latest.sendAmount === "number" && latest.sendAmount > 0
        ? latest.sendAmount
        : SENTINEL_AMOUNT;
    return {
      originCountryCode: originCountry,
      destinationCountryCode: destinationCountry,
      amount,
      anchor: "send",
      sourceCurrency: latest.sourceCurrency || "USD",
      destinationCurrency: latest.destinationCurrency || "MXN",
      include: ["summary"],
    };
  }, [latest, destinationCountry, originCountry]);

  const { result, loading, error } = usePreQuote(input);

  const best = result?.summary.best;
  const rate = best?.customerFxRate;
  const currency =
    best?.destinationCurrency || latest?.destinationCurrency || "";

  if (!latest || !destinationCountry || error || (rate == null && !loading)) {
    return { card: null, loading: false };
  }

  if (rate == null) {
    return {
      card: {
        countryIso2: destinationCountry,
        rate: "…",
        currency,
      },
      loading: true,
    };
  }

  return {
    card: {
      countryIso2: destinationCountry,
      rate: formatRate(rate),
      currency,
    },
    loading: false,
  };
};
